import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Camera, CalendarCheck, Gift, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Panel, inputCls, itDate } from "@/components/kit";
import { api, errMsg, fmtMoney } from "@/lib/api";

const STATO = { richiesta: "in attesa di conferma", approvata: "confermato dallo staff", rifiutata: "non disponibile" };

function Shop({ token, x, setX }) {
  const [q, setQ] = useState({});
  const ask = async (it) => {
    try {
      const r = await api.post(`/public/portal/${token}/extras`, { item_id: it.id, quantita: q[it.id] || 1 });
      setX({ ...x, requests: r.data });
      toast.success("Richiesta inviata: lo staff la conferma e la aggiunge al contratto");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!x.items.length && !x.requests.length) return null;
  return (
    <Panel title="Rendete la festa ancora più speciale" testId="portal-extras">
      <div className="grid sm:grid-cols-2 gap-3">
        {x.items.map((it) => (
          <div key={it.id} className="rounded-xl border border-[#D4E6EC] p-4 space-y-2" data-testid={`portal-extra-${it.id}`}>
            <div className="flex justify-between gap-2"><b className="text-[#0D2B3A]">{it.nome}</b><span className="text-sm text-[#0B6E8F] whitespace-nowrap">{fmtMoney(it.prezzo)} {it.unita}</span></div>
            {it.descrizione && <p className="text-xs text-[#47606B] whitespace-pre-line">{it.descrizione}</p>}
            <div className="flex gap-2 items-center">
              {it.unita !== "forfait" && <input type="number" min="1" className={`${inputCls} w-24`} value={q[it.id] || ""} placeholder="Quantità" onChange={(e) => setQ({ ...q, [it.id]: Number(e.target.value) })} data-testid={`portal-extra-qty-${it.id}`} />}
              <Button size="sm" className="rounded-full" onClick={() => ask(it)} data-testid={`portal-extra-ask-${it.id}`}><ShoppingBag size={13} className="mr-1" /> Lo vorrei</Button>
            </div>
          </div>
        ))}
      </div>
      {x.requests.length > 0 && <div className="text-sm space-y-1 border-t border-[#EAF5F7] pt-3">{x.requests.map((r) => <div key={r.id} data-testid={`portal-xreq-${r.id}`}>• {r.nome} × {r.quantita} — <i>{STATO[r.stato]}</i></div>)}</div>}
    </Panel>
  );
}

export function ClientExtras({ token, signed }) {
  const [x, setX] = useState(null);
  useEffect(() => {
    api.get(`/public/portal/${token}/extras`).then((r) => setX(r.data));
  }, [token]);
  if (!x) return null;
  const t = new Date().toISOString().slice(0, 10);
  const photoUrl = `${window.location.origin}/foto/${x.photo_token}`;
  const friendUrl = `${window.location.origin}/richiesta?amico=${x.referral_code}`;
  return (
    <>
      {x.scadenze.length > 0 && (
        <Panel title="Le prossime scadenze" testId="portal-deadlines">
          {x.scadenze.map((s) => {
            const days = s.data ? Math.ceil((new Date(s.data) - new Date(t)) / 86400000) : null;
            return (
              <div key={s.key} className="flex items-center justify-between text-sm border-t border-[#EAF5F7] pt-2" data-testid={`portal-deadline-${s.key}`}>
                <span className="flex items-center gap-2"><CalendarCheck size={15} className={s.fatto ? "text-[#166534]" : "text-[#0B6E8F]"} />{s.titolo}</span>
                <span className={s.fatto ? "text-[#166534]" : days !== null && days <= 3 ? "text-[#B91C1C] font-medium" : "text-[#47606B]"}>{s.fatto ? "fatto ✓" : `entro il ${itDate(s.data)}${days !== null ? ` • ${days > 0 ? `mancano ${days} giorni` : "scaduta"}` : ""}`}</span>
              </div>
            );
          })}
        </Panel>
      )}
      <Shop token={token} x={x} setX={setX} />
      <Panel title="Le foto dei vostri invitati" testId="portal-photos">
        <p className="text-sm text-[#47606B]">Il giorno della festa troverete un QR sui tavoli: gli invitati caricano le foto e voi le avrete tutte in un unico album.</p>
        <a href={photoUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-[#0B6E8F] hover:underline" data-testid="portal-photo-link"><Camera size={15} /> Apri l'album</a>
      </Panel>
      {signed && x.referral_code && (
        <Panel title="Porta un amico" testId="portal-referral">
          <p className="text-sm text-[#47606B]">Conoscete qualcuno che organizza un evento? Mandategli il vostro link: se firma con noi{x.premio ? <> ricevete <b>{x.premio}</b></> : " vi aspetta un nostro omaggio"}. 🎁</p>
          <div className="text-sm">Il vostro codice: <b data-testid="portal-referral-code">{x.referral_code}</b></div>
          <a href={`https://api.whatsapp.com/send?text=${encodeURIComponent(`Ciao! 😊 Per il tuo evento ti consiglio questa location: chiedi un preventivo da qui 👉 ${friendUrl}`)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-4 py-2 text-sm font-medium" data-testid="portal-referral-wa"><Gift size={14} /> Invia su WhatsApp</a>
        </Panel>
      )}
    </>
  );
}
